import { useEffect, useRef, useState } from "react";
import api, { errMsg } from "../lib/api";
import { toast } from "../lib/notices";
import { Play, Square, Loader2 } from "lucide-react";

/*
 * Hear a cloned voice before committing a render to it.
 *
 * Enrolment returns a voice id and nothing audible, so judging a clone
 * previously meant rendering a whole video with it. The server caches the
 * synthesised line per (voice, language), so the first press may wait on a
 * cold RunPod worker and later ones are immediate — hence the explicit
 * loading state rather than an optimistic play.
 */
export default function VoicePreviewButton({ provider, voiceId, language = "en", label }) {
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  // A blob URL outlives the component unless it is revoked, and an <audio>
  // left playing outlives the row it belonged to.
  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const stop = () => {
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false);
  };

  const play = async () => {
    if (playing) { stop(); return; }
    setBusy(true);
    try {
      const r = await api.post(
        `/providers/my-voices/${encodeURIComponent(provider)}/${encodeURIComponent(voiceId)}/preview`,
        null,
        { params: { language }, responseType: "blob" },
      );
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = URL.createObjectURL(r.data);
      const el = new Audio(urlRef.current);
      audioRef.current = el;
      el.onended = () => setPlaying(false);
      el.onerror = () => { setPlaying(false); toast.error("Could not play the preview."); };
      await el.play();
      setPlaying(true);
    } catch (err) {
      toast.error(errMsg(err, "Could not preview this voice"));
    } finally {
      setBusy(false);
    }
  };

  const name = label ? `${playing ? "Stop" : "Preview"} ${label}` : (playing ? "Stop preview" : "Preview voice");
  return (
    <button
      type="button"
      onClick={play}
      disabled={busy}
      className="brutal-btn brutal-btn-secondary"
      data-testid={`btn-preview-voice-${voiceId}`}
      aria-label={name}
      title={name}
    >
      {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
        : playing ? <Square size={14} aria-hidden="true" />
        : <Play size={14} aria-hidden="true" />}
    </button>
  );
}
